import { Button, Stack } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { find, has, isEmpty, isNil } from 'lodash';
import * as PATHS from 'js/constants/pathnames';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import SettingsFormLayout, { SettingsBlock } from 'new/components/settings/settingsFormLayout';
import { setContextualSupport, unsetContextualSupport, updateOrgInfo } from 'js/actions';
import { GET_ALL_ORGS_REQUEST, UPDATE_ORG_INFO_REQUEST } from 'js/constants/actionTypes';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { TextField } from 'new/elements/TextField';
import styles from './OrganizationInfo.module.scss';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import Typography from 'new/elements/Typography';
import { getUserRoleLabel } from 'js/constants/userRoles';
import OrganizationTrialStatus from 'src/new/components/OrganizationTrialStatus';
import { SUPPORT_KEY_ACCOUNT } from 'js/constants/supportPageKeys';

const fullNameSx = { flex: 1, marginRight: '16px' };

export function OrganizationInfo() {
  const dispatch = useDispatch<any>();
  const user = useCurrentUser();
  const currentOrg = useCurrentOrg();

  const isLoading = useIsLoading(UPDATE_ORG_INFO_REQUEST, GET_ALL_ORGS_REQUEST);

  const {
    register,
    reset,
    formState: { errors, dirtyFields },
    setValue,
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: currentOrg.name,
      email_domain: currentOrg.email_domain,
      description: currentOrg.description,
      members_default_access_role: currentOrg.members_default_access_role,
      client_identifier: currentOrg.client_identifier,
    },
  });

  useEffect(() => {
    setValue('name', currentOrg.name);
    setValue('email_domain', currentOrg.email_domain);
    setValue('description', currentOrg.description);
    setValue('members_default_access_role', currentOrg.members_default_access_role);
    setValue('client_identifier', currentOrg.client_identifier);
  }, [currentOrg]);

  const orgMembership =
    has(user, 'org_memberships') && !isEmpty(user.org_memberships)
      ? find(user.org_memberships, (o) => {
          return o.id === currentOrg.id;
        })
      : null;

  const isOrgAdmin =
    !isNil(orgMembership) && has(orgMembership, 'is_admin?') ? orgMembership['is_admin?'] : false;

  const onSubmit = useCallback(
    (data, e) => {
      const { attribute } = e.nativeEvent.submitter.dataset;

      Object.keys(data).forEach((key) => {
        if (data[key] === null || key !== attribute) {
          delete data[key];
        }
      });

      dispatch(updateOrgInfo(currentOrg.id, data)).then(() => {
        reset(data);
      });
    },
    [currentOrg],
  );

  useEffect(() => {
    dispatch(setContextualSupport(SUPPORT_KEY_ACCOUNT));
    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_ACCOUNT));
    };
  }, []);

  return (
    <PageLayout
      loading={isLoading}
      title="Organization Info"
      titleAccessory={<OrganizationTrialStatus type="inline" />}
      description="As an Org Admin, this is where you can manage Organization details."
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SettingsFormLayout>
          <SettingsBlock
            label="Organization name"
            description="A human-friendly name for the organization"
          >
            <Stack direction="row" alignItems="center">
              <TextField
                sx={fullNameSx}
                type="text"
                label="Name"
                error={!!errors.name}
                helperText={errors.name ? <>{errors.name?.message}</> : undefined}
                {...register('name')}
                disabled={!isOrgAdmin}
              />
              {isOrgAdmin && (
                <Button
                  data-attribute="name"
                  disabled={!dirtyFields.name}
                  className={styles.submit}
                  variant="contained"
                  type="submit"
                >
                  Save
                </Button>
              )}
            </Stack>
          </SettingsBlock>
          <SettingsBlock label="Role" description="My membership in organization">
            <Stack className={styles.emailContainer}>
              <Typography>
                {getUserRoleLabel(find(user.org_memberships, { id: currentOrg.id })?.access_role)}
              </Typography>
            </Stack>
          </SettingsBlock>
          <SettingsBlock label="Domain" description="Company email domain">
            <Stack direction="row" alignItems="center">
              <TextField
                sx={fullNameSx}
                type="text"
                label="Domain"
                error={!!errors.email_domain}
                helperText={errors.email_domain ? <>{errors.email_domain?.message}</> : undefined}
                {...register('email_domain')}
                disabled
              />
              {isOrgAdmin && (
                <Button
                  data-attribute="email_domain"
                  disabled={!dirtyFields.email_domain}
                  className={styles.submit}
                  variant="contained"
                  type="submit"
                >
                  Save
                </Button>
              )}
            </Stack>
          </SettingsBlock>
          <SettingsBlock label="Description" description="Company's longer description">
            <Stack direction="row" alignItems="center">
              <TextField
                sx={fullNameSx}
                type="text"
                label="Description"
                error={!!errors.description}
                helperText={errors.description ? <>{errors.description?.message}</> : undefined}
                {...register('description')}
                disabled={!isOrgAdmin}
              />
              {isOrgAdmin && (
                <Button
                  data-attribute="description"
                  disabled={!dirtyFields.description}
                  className={styles.submit}
                  variant="contained"
                  type="submit"
                >
                  Save
                </Button>
              )}
            </Stack>
          </SettingsBlock>
          <SettingsBlock label="Client identifier" description="Identification of the client">
            <Stack direction="row" alignItems="center">
              <TextField
                sx={fullNameSx}
                type="text"
                label="Identifier"
                error={!!errors.client_identifier}
                helperText={
                  errors.client_identifier ? <>{errors.client_identifier?.message}</> : undefined
                }
                {...register('client_identifier')}
                disabled={!isOrgAdmin}
              />
              {isOrgAdmin && (
                <Button
                  data-attribute="client_identifier"
                  disabled={!dirtyFields.client_identifier}
                  className={styles.submit}
                  variant="contained"
                  type="submit"
                >
                  Save
                </Button>
              )}
            </Stack>
          </SettingsBlock>
        </SettingsFormLayout>
      </form>
    </PageLayout>
  );
}

OrganizationInfo.screenConfig = {
  path: `${PATHS.ROUTE_REDESIGN}/settings/organization-info`,
  screenName: 'OrganizationInfo',
  header: {
    title: 'Organization Info',
    slugTitle: 'Organization Info',
  },
  domain: 'settings',
};
